import { headingAnchors, headingFragment, type WikiPageSummary } from "@esi/shared";
import { ArrowLeft, ArrowRight, ChevronRight, FileText, Folder } from "lucide-react";
import { type MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { titleHeadingIndex } from "@/components/markdown/Markdown";
import { parseMarkdown } from "@/components/markdown/parse";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useFolders } from "../projects/folders.ts";
import { folderNodeId, knowledgeTree, wikiFolderIndex } from "../projects/knowledge-tree.ts";
import { Link, wikiPath } from "../router.tsx";
import { useWikiIndex } from "./queries.ts";
import { useFolderNeighbours } from "./structure.ts";

// A page's navigation (wiki-collaboration-ux D10), shared by the full page and the wiki side
// panel: the breadcrumb with each folder's pages in a popover, previous and next in its folder,
// and On this page. Everything comes from the reader-filtered page index and the folder list the
// tree already loads: a page the viewer cannot read is never named. In the panel, `onOpen` keeps
// a followed page in the panel; elsewhere links navigate.

type Open = ((pageId: string) => void) | undefined;

/** A plain click on a page link opens it in place when `onOpen` is given. */
const opener = (onOpen: Open, pageId: string) => (event: MouseEvent<HTMLAnchorElement>) => {
  // Already handled (the panel's own capture opens wiki links in place): never twice.
  if (
    !onOpen ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }
  event.preventDefault();
  onOpen(pageId);
};

/** The page's folders from the Wiki root, each with its ID; empty at the root or while loading. */
function useFolderTrail(projectId: string, folderId: string | null | undefined) {
  const pages = useWikiIndex(projectId).data?.pages;
  const folders = useFolders(projectId).data?.folders;
  return useMemo(() => {
    if (!folderId || !pages || !folders) {
      return [];
    }
    const tree = knowledgeTree({ pages, folders });
    const trail: { id: string; name: string }[] = [];
    for (let id: string | null = folderId; id; ) {
      const node = tree.byId.get(folderNodeId(id));
      if (node?.kind !== "folder" || node.section !== "wiki") {
        break;
      }
      trail.unshift({ id: node.folder.id, name: node.folder.name });
      id = node.folder.parentId ?? null;
    }
    return trail;
  }, [folderId, pages, folders]);
}

/** "Wiki › Procedures › Lubrication": each folder opens its pages in a popover (the tree's). */
export function PageBreadcrumb(props: {
  projectId: string;
  page: Pick<WikiPageSummary, "id" | "folderId">;
  onOpen?: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const trail = useFolderTrail(props.projectId, props.page.folderId);
  return (
    <nav aria-label={t("wiki.breadcrumb.label")} data-esi-content="wiki">
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-fg-muted">
        <li>
          <Link
            href={wikiPath(props.projectId)}
            className="rounded-sm underline-offset-4 hover:text-fg hover:underline"
          >
            {t("wiki.breadcrumb.wiki")}
          </Link>
        </li>
        {trail.map((folder) => (
          <li key={folder.id} className="flex min-w-0 items-center gap-x-1">
            <ChevronRight aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            <FolderPopover
              projectId={props.projectId}
              folderId={folder.id}
              name={folder.name}
              current={props.page.id}
              onOpen={props.onOpen}
            />
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** A folder of the breadcrumb: its subfolders and pages, the current page marked. */
function FolderPopover(props: {
  projectId: string;
  folderId: string;
  name: string;
  current: string;
  onOpen: Open;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const pages = useWikiIndex(props.projectId).data?.pages;
  const folders = useFolders(props.projectId).data?.folders;
  const index = pages && folders ? wikiFolderIndex(pages, folders, props.folderId) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="max-w-48 truncate rounded-sm hover:text-fg hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-label={t("wiki.breadcrumb.folder", { folder: props.name })}
        >
          {props.name}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        <p className="px-2 pt-1 pb-1.5 text-xs font-medium text-fg-muted">
          {t("wiki.breadcrumb.folder", { folder: props.name })}
        </p>
        {index && (index.subfolders.length || index.pages.length) ? (
          <ul className="flex max-h-72 flex-col overflow-y-auto">
            {index.subfolders.map((f) => (
              <li key={f.id}>
                <Link
                  href={`${wikiPath(props.projectId)}?folder=${encodeURIComponent(f.id)}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg-secondary hover:bg-surface-hover"
                >
                  <Folder aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{f.name}</span>
                </Link>
              </li>
            ))}
            {index.pages.map((p) => (
              <li key={p.id}>
                <Link
                  href={wikiPath(props.projectId, p.id)}
                  aria-current={p.id === props.current ? "page" : undefined}
                  onClick={(e) => {
                    opener(props.onOpen, p.id)(e);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg-secondary hover:bg-surface-hover aria-[current=page]:bg-surface-selected aria-[current=page]:font-medium aria-[current=page]:text-fg"
                >
                  <FileText aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{p.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-1.5 text-sm text-fg-muted">{t("wiki.breadcrumb.empty")}</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Previous and next pages of the page's folder, in the tree's order (spec `project-wiki`
 * "Previous and next page"); nothing for a page alone in its folder.
 */
export function FolderNeighbours(props: {
  projectId: string;
  pageId: string;
  onOpen?: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const { previous, next } = useFolderNeighbours(props.projectId, props.pageId);
  if (!previous && !next) {
    return null;
  }
  const link =
    "group flex min-w-0 flex-col gap-1 rounded-md px-3 py-2.5 -mx-3 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <nav
      data-esi-content="wiki"
      aria-label={t("wiki.neighbours.label")}
      className="grid gap-2 border-t border-line pt-4 sm:grid-cols-2 sm:gap-6"
    >
      {previous ? (
        <Link
          href={wikiPath(props.projectId, previous.id)}
          onClick={opener(props.onOpen, previous.id)}
          className={link}
        >
          <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
            <ArrowLeft aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
            {t("wiki.neighbours.previous")}
          </span>
          <span className="text-sm font-medium break-words text-fg">{previous.title}</span>
        </Link>
      ) : null}
      {next ? (
        <Link
          href={wikiPath(props.projectId, next.id)}
          onClick={opener(props.onOpen, next.id)}
          className={`${link} sm:col-start-2 sm:items-end sm:text-right`}
        >
          <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
            {t("wiki.neighbours.next")}
            <ArrowRight aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
          </span>
          <span className="text-sm font-medium break-words text-fg">{next.title}</span>
        </Link>
      ) : null}
    </nav>
  );
}

/** The page's headings as links to their sections (`prefix` is the renderer's id prefix). */
export function OnThisPage(props: { markdown: string; title: string; prefix: string }) {
  const { t } = useTranslation();
  // The title heading the article leaves out is not a section of it (the renderer's own rule).
  const omitted = titleHeadingIndex(parseMarkdown(props.markdown), props.title);
  const headings = headingAnchors(props.markdown).filter((h) => h.block !== omitted);
  if (!headings.length) {
    return null;
  }
  return (
    <nav aria-label={t("wiki.panel.onThisPage")} data-esi-content="wiki">
      <h3 className="mb-1.5 text-xs font-medium text-fg-muted">{t("wiki.panel.onThisPage")}</h3>
      <ul className="flex flex-col gap-0.5 text-[13px]">
        {headings.map((h) => {
          const id = `${props.prefix}-${headingFragment(h)}`;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById(id)
                    ?.scrollIntoView({ block: "start", behavior: "smooth" });
                }}
                className="block truncate rounded-sm py-0.5 text-fg-secondary hover:text-fg hover:underline underline-offset-4"
              >
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
